import React, { useEffect, useState, useCallback, useRef } from "react";
import linkedin from "../../images/linkedin-logo.png";
import axios from "axios";
import { useParams } from "react-router-dom";
import cv from "../../images/cv-icon.png";
import github from "../../images/github.png";
import jumpstart from "../../images/jumpstart_logo.jpeg";
import Loading from "../../images/loading-black.svg";
import { useNavigate } from "react-router-dom";
import Office from "../../images/office-icon.png";
import Languages from "../../images/languages-icon.png";
import save from "../../images/save-icon.png";
import { useShortlisted } from "../../provider/ShortlistedContext";
import LoadingWhite from "../../images/loading-white.svg";
import LoadingBlack from "../../images/loading-black.svg";
import VideoIntro from "../../images/video-icon-green.png";
import RoleMatchRequestModal from "../../components/modals/RoleMatchRequestModal";


/**
 * Returns a custom sponsorship/visa message depending on the "RTW doc" value.
 */
function getVisaMessage(visaStatus, fullName = "", undergradGradYear = null, postgradGradYear = null) {
  const firstName = fullName.split(" ")[0]; // e.g. "Jane Doe" -> "Jane"
  const jumpstartEmail = "operations@jumpstart-uk.com";
  const contactTeamMsg = `Contact the <a href="mailto:${jumpstartEmail}" class="underline">Jumpstart team</a> if you have any concerns.`;

  switch (visaStatus) {
    case "Yes - Post Study Visa":
      const uGradYear = undergradGradYear ? parseInt(undergradGradYear, 10) : null;
      const pGradYear = postgradGradYear ? parseInt(postgradGradYear, 10) : null;
      
      // If EITHER graduation year is 2024 or later, the candidate is recent, so we don't show the warning.
      if ((uGradYear && uGradYear >= 2024) || (pGradYear && pGradYear >= 2024)) {
        return ""; // Don't show for 2024+ grads with PSW
      }
      return `${firstName} has a two-year post-study visa, so may require sponsorship in future. ${contactTeamMsg}`;
    case "No - I require Visa sponsorship to work in the UK":
      return `${firstName} needs visa sponsorship to work in the UK. ${contactTeamMsg}`;
    case "Yes - High Potential Individual Visa":
      return `${firstName} has a two-year high performance individual visa, so may require sponsorship in future. ${contactTeamMsg}`;
    case "Yes - Skilled Worker Visa":
    case "No - Requires Visa sponsorship":
      return `${firstName} requires visa sponsorship to work in the UK. Contact the <a href="mailto:${jumpstartEmail}" class="underline">Jumpstart team</a> for future details.`;
    case "Other":
      return `${firstName} may require visa sponsorship, please confirm directly with the <a href="mailto:${jumpstartEmail}" class="underline">Jumpstart team</a> if concerned.`;
    default:
      // If British or Irish passport, show nothing:
      if (
        visaStatus === "Yes - British Passport" ||
        visaStatus === "Yes - Irish Passport" ||
        visaStatus === "Yes - Settlement Status" ||
        visaStatus === "Yes - British National (Overseas) Visa" ||
        visaStatus === "Yes - Settlement Status / Indefinite Leave to Remain" ||
        visaStatus === "Yes - Dependent Visa (e.g., Spouse/Partner Visa)" ||
        visaStatus === "Yes - Ancestry Visa"
      ) {
        return "";
      }
      // Fallback for anything else:
      if (visaStatus) {
        const cleanedStatus = visaStatus.replace("Yes - ", "");
        return `${firstName} has ${cleanedStatus} and may require visa sponsorship in the future. ${contactTeamMsg}`;
      }
      return "";
  }
}

function Candidate() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [error, setError] = useState(null);
  const [candidateRoles, setCandidateRoles] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rolesNeedUpdate, setRolesNeedUpdate] = useState(true);
  const [roles, setRoles] = useState([]);
  const [shortlistedCandidates, setShortlistedCandidates] = useState(null);
  const { shortlistedCount, setShortlistedCount } = useShortlisted();
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const roleMenuRef = useRef(null);
  const [newRoleTitle, setNewRoleTitle] = useState("");
  const [newRoleForm, setNewRoleForm] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [showJumpstartNotes, setShowJumpstartNotes] = useState(false);

  // Match request state variables (added exactly like in GeneralistCard)
  const [showMatchRequestModal, setShowMatchRequestModal] = useState(false);
  const [showMatchRequestRoleMenu, setShowMatchRequestRoleMenu] = useState(false);
  const [selectedRoleForModal, setSelectedRoleForModal] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [candidatesStatus, setCandidatesStatus] = useState([]);
  const [hasMatchRequest, setHasMatchRequest] = useState(false);
  const [showVisaTooltip, setShowVisaTooltip] = useState(false);
  const matchRequestRoleMenuRef = useRef();

  // Similar candidates state variables
  const [similarCandidates, setSimilarCandidates] = useState([]);
  const [similarCandidatesLoading, setSimilarCandidatesLoading] = useState(false);
  const [similarCandidatesError, setSimilarCandidatesError] = useState(null);
  const [isSimilarCandidatesExpanded, setIsSimilarCandidatesExpanded] = useState(false);

  // Local state to track application status changes
  const [localApplicationStatus, setLocalApplicationStatus] = useState(null);

  // Your existing language icons map
  const languageLogos = {
    JavaScript: "/images/coding_logo/javascript.png",
    Python: "/images/coding_logo/python.png",
    C: "/images/coding_logo/c.png",
    Swift: "/images/coding_logo/swift.png",
    "C#": "/images/coding_logo/csharp.png",
    "C++": "/images/coding_logo/cplusplus.png",
    Java: "/images/coding_logo/java.svg",
    Go: "/images/coding_logo/go.png",
    PHP: "/images/coding_logo/php.svg",
    Ruby: "/images/coding_logo/ruby.webp",
    Kotlin: "/images/coding_logo/kotlin.png",
    Rust: "/images/coding_logo/rust.png",
    Clojure: "/images/coding_logo/clojure.png",
    Haskell: "/images/coding_logo/haskell.png",
    Dart: "/images/coding_logo/dart.png",
    Elixir: "/images/coding_logo/elixir.webp",
    "Objective-C": "/images/coding_logo/objc.svg",
    Perl: "/images/coding_logo/perl.png",
    Scala: "/images/coding_logo/scala.png",
    Typescript: "/images/coding_logo/typescript.png",
    React: "/images/coding_logo/react.png",
    Vue: "/images/coding_logo/vue.png",
    Angular: "/images/coding_logo/angular.png",
    "Next.js": "/images/coding_logo/next-js.svg",
    MATLAB: "/images/coding_logo/matlab.png"

  };

  // Status styles for application states (added from generalist_card.js)
  const statusStyles = {
    "Application pending": {
      color: "text-green-800",
      borderColor: "border-green-300",
      bgColor: "bg-green-50",
      message: (candidate, role) => `${candidate.name} applied to ${role}`,
    },
    Interviewing: {
      color: "text-blue-800",
      borderColor: "border-blue-300",
      bgColor: "bg-blue-50",
      message: (candidate, role) =>
        `${candidate.name} is being interviewed for ${role}`,
    },
    "Match requested": {
      color: "text-orange-800",
      borderColor: "border-orange-300",
      bgColor: "bg-orange-50",
      message: (candidate, role) =>
        `You have sent a request to ${candidate.name} for ${role}`,
    },
    "Rejected @ CV": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) =>
        `You rejected ${candidate.name} for ${role}`,
    },
    "Rejected @ Interview": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) =>
        `You rejected ${candidate.name} for ${role}`,
    },
    "Rejected Interview Request": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) =>
        `${candidate.name} declined invite to ${role}`,
    },
    "Company withdrawn": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) => `You withdrew from ${role}`,
    },
    "Candidate withdrawn": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) => `${candidate.name} withdrew from ${role}`,
    },
  };

  // (You can remove if no longer needed)
  const validRightToWorkStatuses = [
    "No - I don't have the right to work in the UK",
    "No - Requires Visa sponsorship",
    "No - Looking for Visa sponsorship, but willing to self-sponsor if necessary",
    "No - Requires visa sponsorship - is well versed in the process + has a lawyer at hand if needed",
  ];

  const handleAddToShortlist = () => {
    setShowRoleMenu(!showRoleMenu);
  };

  // Match request handlers (added exactly like in GeneralistCard)
  const handleMatchRequestClick = () => {
    // If there's already a request, don't show the menu
    if (hasMatchRequest) return;
    
    setShowMatchRequestRoleMenu(!showMatchRequestRoleMenu);
    if (showRoleMenu) setShowRoleMenu(false);
    if (showMatchRequestModal) setShowMatchRequestModal(false);
  };

  const handleMatchRequestRoleSelect = async (role) => {
    setRequestLoading(true);
    setSelectedRoleForModal(role);
    setShowMatchRequestRoleMenu(false);
    setShowMatchRequestModal(true);
    setTimeout(() => setRequestLoading(false), 500); // Simulate loading for modal open
  };

  const handleRequestSent = (candidateId, roleId) => {
    if (!setRoles || !roles) return;
    setRoles(prevRoles => {
      if (!Array.isArray(prevRoles)) return prevRoles;
      return prevRoles.map(role => {
        if (role.id === roleId) {
          const prevMR = Array.isArray(role["MR'd Candidates"]) ? role["MR'd Candidates"] : [];
          if (!prevMR.includes(candidateId)) {
            return {
              ...role,
              "MR'd Candidates": [...prevMR, candidateId],
            };
          }
        }
        return role;
      });
    });
  };

  // Check if candidate is in MR'd Candidates for any role
  const isMatchRequestedForRole = (candidateId, roles) => {
    if (!roles) return null;
    // Array format
    if (Array.isArray(roles)) {
      for (const role of roles) {
        if (!role) continue;
        const mrdCandidates = role["MR'd Candidates"];
        if (
          mrdCandidates &&
          Array.isArray(mrdCandidates) &&
          mrdCandidates.includes(candidateId)
        ) {
          return role.id || role["Role Title"] || true;
        }
      }
      return null;
    }
    // Object format
    if (typeof roles === 'object') {
      for (const roleName in roles) {
        const role = roles[roleName];
        if (!role) continue;
        if (
          role["MR'd Candidates"] &&
          Array.isArray(role["MR'd Candidates"]) &&
          role["MR'd Candidates"].includes(candidateId)
        ) {
          return roleName;
        }
      }
    }
    return null;
  };

  const formatDuration = (duration) => {
    if (!duration) return "";
    const years = Number(duration.years) || 0;
    const months = Number(duration.months) || 0;
    
    const totalYears = years + Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    const parts = [];
    if (totalYears > 0) {
      parts.push(`${totalYears} yrs`);
    }
    if (remainingMonths > 0) {
      parts.push(`${remainingMonths} mos`);
    }
    return parts.length > 0 ? parts.join(", ") : "";
  };

  const transformRoles = (rolesToTransform) => {
    const roleSet = new Set(rolesToTransform);
    if (roleSet.size === 1) {
      if (roleSet.has("Full Stack Developer")) return ["Full Stack"];
      if (roleSet.has("Front End Developer")) return ["Front End"];
      if (roleSet.has("Back End Developer")) return ["Back End"];
    }
    if (
      roleSet.has("Front End Developer") &&
      roleSet.has("Back End Developer")
    ) {
      return ["Full Stack"];
    }
    if (roleSet.has("Full Stack Developer")) {
      return ["Full Stack"];
    }
    return rolesToTransform;
  };

  const newRole = () => {
    setNewRoleForm(true);
  };

  // === INTERVIEWS ===
  const fetchInterviews = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get("/api/interviews/fetchInterviews", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const interviews = response.data.interviews || [];

      const transformedCandidate = interviews.map((interview) => {
        // Use the fields structure like in applications.js
        const fields = interview.fields || interview;
        
        let stage;
        if (
          fields["Stage"] === "Interviewing (request accepted)" ||
          fields["Stage"] === "Interviewing (application)"
        ) {
          stage = "Being interviewed";
        } else {
          stage = fields["Stage"];
        }
        
        return {
          id: fields["id"] || interview.id || undefined,
          role_id: fields["Role"]?.[0] || undefined,
          role_title: Array.isArray(fields["Role Title (from Role)"])
            ? fields["Role Title (from Role)"][0]
            : fields["Role Title (from Role)"] || "",
          // Use the Candidate field which contains the record ID
          candidate: Array.isArray(fields["Candidate"])
            ? fields["Candidate"][0]
            : fields["Candidate"] || "",
          stage: stage,
          rationale: fields["Application Rationale"] || undefined,
        };
      });

      setCandidateRoles(transformedCandidate);
    } catch (err) {
      console.error(err);
    }
  };

  // === SHORTLISTS ===
  const fetchShortlistedCandidates = useCallback(async () => {
    if (!rolesNeedUpdate) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/roles/fetchRoles", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const fetchedRoles = response.data.roles || [];
      let shortlistedCandidatesByRole = {};
      let rolesList = [];

      fetchedRoles.forEach((roleObj) => {
        const roleTitle = roleObj["Role Title"];
        const roleUrlId = roleObj["URL ID"];
        const roleId = roleObj["id"];
        rolesList.push(roleTitle);

        if (roleObj["Shortlisted Candidates"]) {
          if (!shortlistedCandidatesByRole[roleTitle]) {
            shortlistedCandidatesByRole[roleTitle] = {
              roleUrlId: roleUrlId,
              roleId: roleId,
              candidates: [],
            };
          }
          shortlistedCandidatesByRole[roleTitle].candidates =
            shortlistedCandidatesByRole[roleTitle].candidates.concat(
              roleObj["Shortlisted Candidates"]
            );
        }
      });

      setShortlistedCandidates(shortlistedCandidatesByRole);
      setLoading(false);
      setButtonLoading(false);
      setRoles(fetchedRoles);
      setRolesNeedUpdate(false);
    } catch (err) {
      console.error(err);
    }
  }, [rolesNeedUpdate]);

  // === FETCH CANDIDATE DATA ===
  useEffect(() => {
    fetchInterviews();
    fetchShortlistedCandidates();

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `/api/candidates/fetchCandidate?id=${id}`
        );
        const candidateRecord = response.data.candidate;

        if (!candidateRecord) {
          throw new Error("No candidate data found");
        }

        const fields = candidateRecord.fields || {};

        let sweRoles = fields["SWE relevant roles"] || "";
        if (sweRoles) {
          sweRoles = Array.isArray(sweRoles)
            ? sweRoles[0].split(",").map((role) => role.trim())
            : sweRoles.split(",").map((role) => role.trim());
          sweRoles = transformRoles(sweRoles);
        } else {
          sweRoles = [];
        }

        const stream = fields["Stream (from CRM)"]?.[0];

        let salary;
        if (stream === "Generalist" || stream === "Marketing") {
          salary = fields["Salary - cleaned"] || "";
        } else if (stream === "Sales") {
          salary = fields["Base range + commission"] || "";
        } else if (stream === "Software Engineering") {
          salary = fields["SWE Salary - cleaned"] || "";
        }

        const transformedData = {
          id: candidateRecord.id,
          url_id: fields["ID"] || "",
          minimum_salary: fields["Minimum salary"] || "",
          ideal_salary: fields["Ideal salary"] || "",
          createdTime: candidateRecord["createdTime"] || "",
          years_of_exp: fields["Years of exp (or commercial exp)"] || 0,
          cv: fields["CV RAW"] || "",
          video: fields["Video intro (new)"] || "",
          linkedin: fields["LinkedIn RAW"] || "",
          industry_exp: fields["Industry experience"] || undefined,
          name: fields["Name"] || "",
          lastName: fields["Surname"] || "",
          undergrad_degree_type: fields["Undergrad degree type"] || "",
          undergrad_degree_subject: fields["Undergrad subject"] || "",
          undergrad_university: fields["Undergrad university"] || "",
          undergrad_graduation_year: fields["Undergrad graduation year"] || "",
          undergrad_result: fields["Undergrad result"] || "",
          postgrad_degree_type: fields["Postgrad degree type"] || "",
          postgrad_degree_subject: fields["Postgrad subject"] || "",
          postgrad_university: fields["Postgrad university"] || "",
          postgrad_graduation_year: fields["Postgrad graduation year"] || "",
          postgrad_result: fields["Postgrad result"] || "",
          roles: fields["Generalist roles suitable for copy"] || "",
          email: fields["Email"] || "",
          image: fields.Photo ? fields.Photo[0].url : null,
          work_experience_1_role: fields["(1) Work Experience Role"] || "",
          work_experience_1_company: fields["(1) Work Experience Company"] || "",
          work_experience_1_description:
            fields["(1) Work Experience Description"] || "",
          work_experience_1_duration: {
            years: fields["(1) Time spent in work experience YRS"] || 0,
            months: fields["(1) Time spent in work experience MOS"] || 0,
          },
          work_experience_2_role: fields["(2) Work Experience Role"] || "",
          work_experience_2_company: fields["(2) Work Experience Company"] || "",
          work_experience_2_description:
            fields["(2) Work Experience Description"] || "",
          work_experience_2_duration: {
            years: fields["(2) Time spent in work experience YRS"] || 0,
            months: fields["(2) Time spent in work experience MOS"] || 0,
          },
          working_style: fields["Preferred working style copy"] || "",
          github: fields["Github RAW"] || "",
          swe_roles: sweRoles,
          languages: fields["Languages copy"] || "",
          time_coding: fields["Time spent coding"] || "",
          coding_languages: fields["Coding languages new"] || "",
          description: fields["Top achievement"] || "",
          mostRecentCohort: fields["Most recent cohort"] || "",
          recentTarget: fields["Recent target achieved"] || "",
          stream: stream || "",
          search_fields: fields["Search Aggregate Fields"] || "",
          sales:
            fields["Proven Sales Experience / High Potential for Sales"] || "",
          exp_swe: fields["Level of exp SWE"] || "",
          portfolio: fields["SWE Portfolio"] || "",
          marketing_portfolio: fields["Marketing portfolio RAW"] || "",

          // === Pull the "RTW doc" as right_to_work ===
          right_to_work: fields["RTW doc"] || "",
          "Jumpstart view (Sales)": fields["Jumpstart view (Sales)"] || "",
        };

        setCandidate(transformedData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setButtonLoading(false);
        setLoading(false);
      }
    };

    fetchData();
  }, [id, rolesNeedUpdate, fetchShortlistedCandidates]);

  // useEffect for match request functionality (added exactly like in GeneralistCard)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleMenuRef.current && !roleMenuRef.current.contains(event.target)) {
        setShowRoleMenu(false);
      }
      if (matchRequestRoleMenuRef.current && !matchRequestRoleMenuRef.current.contains(event.target)) {
        setShowMatchRequestRoleMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check if candidate has a match request
  useEffect(() => {
    if (candidate) {
      // Check if this candidate has a match request in candidatesStatus
      const hasRequest = candidatesStatus.some(
        status => status.id === candidate.id && status.status === "Match requested"
      );
      setHasMatchRequest(hasRequest);
    }
  }, [candidate, candidatesStatus]);

  // Auto-generate similar candidates when candidate data is loaded
  useEffect(() => {
    if (candidate && candidate.id && candidate.stream) {
      // Only generate if we haven't already loaded similar candidates
      if (similarCandidates.length === 0 && !similarCandidatesLoading) {
        findSimilarCandidates(candidate);
      }
    }
  }, [candidate]);

  // Function to find similar candidates (auto-triggered)
  const findSimilarCandidates = async (candidateData) => {
    console.log("=== AUTO-GENERATING SIMILAR CANDIDATES ===");
    console.log("Current candidate:", candidateData);

    if (!candidateData) {
      console.error("No candidate data available");
      setSimilarCandidatesError("No candidate data available");
      return;
    }

    setSimilarCandidatesLoading(true);
    setSimilarCandidatesError(null);
    setSimilarCandidates([]);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      console.log("Making API call to find similar candidates...");
      console.log("Candidate ID:", candidateData.id);
      console.log("Candidate stream:", candidateData.stream);

      // Prepare candidate data for embeddings matching the API format
      const candidateDataForAPI = {
        id: candidateData.id,
        stream: candidateData.stream,
        fields: {
          "Name": candidateData.name,
          "Undergrad degree type": candidateData.undergrad_degree_type,
          "Undergrad subject": candidateData.undergrad_degree_subject,
          "Undergrad university": candidateData.undergrad_university,
          "Undergrad result": candidateData.undergrad_result,
          "Undergrad graduation year": candidateData.undergrad_graduation_year,
          "Postgrad degree type": candidateData.postgrad_degree_type,
          "Postgrad subject": candidateData.postgrad_degree_subject,
          "Postgrad university": candidateData.postgrad_university,
          "Postgrad result": candidateData.postgrad_result,
          "Postgrad graduation year": candidateData.postgrad_graduation_year,
          "(1) Work Experience Role": candidateData.work_experience_1_role,
          "(1) Work Experience Company": candidateData.work_experience_1_company,
          "(1) Work Experience Description": candidateData.work_experience_1_description,
          "(1) Time spent in work experience YRS": candidateData.work_experience_1_duration?.years,
          "(1) Time spent in work experience MOS": candidateData.work_experience_1_duration?.months,
          "(2) Work Experience Role": candidateData.work_experience_2_role,
          "(2) Work Experience Company": candidateData.work_experience_2_company,
          "(2) Work Experience Description": candidateData.work_experience_2_description,
          "(2) Time spent in work experience YRS": candidateData.work_experience_2_duration?.years,
          "(2) Time spent in work experience MOS": candidateData.work_experience_2_duration?.months,
        }
      };

      console.log("Candidate data prepared for API:", candidateDataForAPI);

      const response = await axios.post(
        "/api/candidates/findSimilarCandidates",
        {
          candidateId: candidateData.id,
          candidateData: candidateDataForAPI
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("API Response:", response.data);

      if (response.data.similarCandidates) {
        setSimilarCandidates(response.data.similarCandidates);
        console.log(`Found ${response.data.similarCandidates.length} similar candidates`);
      } else {
        setSimilarCandidates([]);
        console.log("No similar candidates found");
      }

    } catch (error) {
      console.error("Error finding similar candidates:", error);
      setSimilarCandidatesError(error.response?.data?.error || error.message || "Failed to find similar candidates");
    } finally {
      setSimilarCandidatesLoading(false);
    }
  };

  if (error) {
    return <p>Error: {error}</p>;
  }
  if (!candidate) {
    return <p>No candidate data found</p>;
  }

  const handleBackToAllCandidates = () => {
    const lastVisitedPage = sessionStorage.getItem("lastVisitedPage");
    if (lastVisitedPage) {
      navigate(lastVisitedPage);
    } else {
      navigate("/browse-candidates");
    }
  };

  // This function chooses the highlight colors for each stream
  const getColorClasses = (stream) => {
    switch (stream) {
      case "Generalist":
        return {
          bgColor: "bg-emerald-100",
          textColor: "text-emerald-700",
          workBgColor: "bg-emerald-50",
          fillColor: "fill-emerald-700",
          bgColor2: "bg-emerald-400",
          borderColor: "border-emerald-400",
        };
      case "Sales":
        return {
          bgColor: "bg-blue-100",
          textColor: "text-blue-700",
          workBgColor: "bg-blue-50",
          fillColor: "fill-blue-700",
          bgColor2: "bg-blue-400",
          borderColor: "border-blue-400",
        };
      case "Software Engineering":
        return {
          bgColor: "bg-orange-100",
          textColor: "text-orange-700",
          workBgColor: "bg-orange-50",
          fillColor: "fill-orange-700",
          bgColor2: "bg-orange-400",
          borderColor: "border-orange-400",
        };
      case "Marketing":
        return {
          bgColor: "bg-purple-100",
          textColor: "text-purple-700",
          workBgColor: "bg-purple-50",
          fillColor: "fill-purple-700",
          bgColor2: "bg-purple-400",
          borderColor: "border-purple-400",
        };
      default:
        return {
          bgColor: "bg-gray-100",
          textColor: "text-gray-700",
          workBgColor: "bg-gray-50",
          fillColor: "fill-gray-700",
          bgColor2: "bg-gray-400",
          borderColor: "border-gray-400",
        };
    }
  };

  const { bgColor, textColor, workBgColor, fillColor, bgColor2, borderColor } =
    getColorClasses(candidate.stream);

  const handleNewRoleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      fields: {
        "Role Title": newRoleTitle,
      },
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post("/api/roles/createRole", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const newRoleObj = response.data.newRoleData;
      handleRoleSelect(newRoleObj, candidate.id);
      setRolesNeedUpdate(true);
      setNewRoleForm(false);
      setNewRoleTitle("");
    } catch (err) {
      console.error(err);
    }
  };

  const handleRoleSelect = async (role, candidateId) => {
    setButtonLoading(true);

    const currentShortlistedCandidates = role["Shortlisted Candidates"] || [];
    const currentCandidatesShortlistedBefore = role["Candidates Shortlisted Before"] || [];
    
    const updatedShortlistedCandidates = [
      ...new Set([...currentShortlistedCandidates, candidateId]),
    ];
    
    // Add to historical tracking field (no duplicates)
    const updatedCandidatesShortlistedBefore = [
      ...new Set([...currentCandidatesShortlistedBefore, candidateId]),
    ];

    const updatedData = {
      fields: {
        "Shortlisted Candidates": updatedShortlistedCandidates,
        "Candidates Shortlisted Before": updatedCandidatesShortlistedBefore,
      },
      roleId: role.id,
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        "/api/roles/updateRoleData",
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const updatedRoleData = response.data.updatedRoleData.fields;
      setRoles((prevRoles) => {
        return prevRoles.map((r) =>
          r.id === updatedRoleData.id ? updatedRoleData : r
        );
      });

      // recalc new total
      const newShortlistedCount = roles.reduce(
        (acc, r) =>
          acc +
          (r["Shortlisted Candidates"] ? r["Shortlisted Candidates"].length : 0),
        0
      );
      setShortlistedCount(newShortlistedCount);

      setRolesNeedUpdate(true);
      fetchShortlistedCandidates();
    } catch (err) {
      console.error(err);
    } finally {
      setButtonLoading(false);
      setShowRoleMenu(false);
    }
  };

  const handleRemoveCandidate = async (shortlistedDict, roleKey, candidateId) => {
    setButtonLoading(true);
    const currentShortlistedCandidates =
      shortlistedDict[roleKey]["candidates"] || [];
    const updatedShortlistedCandidates = currentShortlistedCandidates.filter(
      (cand) => cand !== candidateId
    );

    const updatedData = {
      fields: {
        "Shortlisted Candidates": updatedShortlistedCandidates,
      },
      roleId: shortlistedDict[roleKey].roleId,
    };

    try {
      const token = localStorage.getItem("token");
      await axios.patch("/api/roles/updateRoleData", updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const newShortlistedCount = shortlistedCount - 1;
      setShortlistedCount(newShortlistedCount);
      setRolesNeedUpdate(true);
      fetchShortlistedCandidates();
    } catch (err) {
      console.error(err);
    } finally {
      setShowRoleMenu(false);
      setButtonLoading(false);
    }
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const isShortlistedForRole = (candId, dictOfRoles) => {
    for (const roleName in dictOfRoles) {
      if (dictOfRoles[roleName].candidates.includes(candId)) {
        return roleName;
      }
    }
    return null;
  };

  const roleShortlistedFor = shortlistedCandidates
    ? isShortlistedForRole(candidate.id, shortlistedCandidates)
    : null;

  // Check if candidate is match requested for any role (added like in GeneralistCard)
  const roleMatchRequestedFor = roles ? isMatchRequestedForRole(candidate.id, roles) : null;

  // Visa message calculation
  const visaMessage =
    candidate && candidate.right_to_work
      ? getVisaMessage(candidate.right_to_work, candidate.name, candidate.undergrad_graduation_year, candidate.postgrad_graduation_year)
      : "";
  
  // Debug logging for visa status
  console.log("Visa Debug:", {
    candidateName: candidate?.name,
    rightToWork: candidate?.right_to_work,
    undergradYear: candidate?.undergrad_graduation_year,
    postgradYear: candidate?.postgrad_graduation_year,
    visaMessage: visaMessage,
    visaMessageLength: visaMessage?.length
  });

  // Helper function to get the embedded video URL
  const getEmbeddedVideoUrl = (url) => {
    if (!url) return null;
    
    // Handle Loom videos
    if (url.includes('loom.com')) {
      // Convert from https://www.loom.com/share/[id] to https://www.loom.com/embed/[id]
      return url.replace('/share/', '/embed/');
    }
    
    // Handle Vimeo videos
    if (url.includes('vimeo.com')) {
      // Extract the Vimeo ID
      const vimeoId = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoId && vimeoId[1]) {
        return `https://player.vimeo.com/video/${vimeoId[1]}`;
      }
    }
    
    // Handle YouTube videos
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      // Extract YouTube ID
      let youtubeId = '';
      if (url.includes('youtube.com/watch')) {
        const searchParams = new URL(url).searchParams;
        youtubeId = searchParams.get('v');
      } else if (url.includes('youtu.be/')) {
        youtubeId = url.split('youtu.be/')[1];
      }
      
      if (youtubeId) {
        return `https://www.youtube.com/embed/${youtubeId}`;
      }
    }
    
    // Return original URL if we can't transform it
    return url;
  };

  // Toggle video playback
  const toggleVideo = (e) => {
    e.stopPropagation(); // Prevent card click navigation
    if (!isVideoPlaying) {
      setIsVideoLoading(true); // Set loading state when starting to play
    }
    setIsVideoPlaying(!isVideoPlaying);
  };
  
  // Handle video loaded
  const handleVideoLoaded = () => {
    setIsVideoLoading(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center bg-white pb-20">
        <div className="max-w-[700px] flex flex-col h-full mt-20 bg-white animate-pulse">
          <div className="h-[400px] w-[700px] bg-gray-300 rounded-xl"></div>
          <div className="h-10 w-48 bg-gray-300 rounded mt-2"></div>
          <div className="flex h-6 mt-2">
            <div className="h-6 w-20 mr-2 bg-gray-300 rounded"></div>
            <div className="h-6 w-24 mr-2 bg-gray-300 rounded"></div>
            <div className="h-6 w-24 mr-2 bg-gray-300 rounded"></div>
          </div>
          <div className="flex h-6 mt-2">
            <div className="h-6 w-60 mr-2 bg-gray-300 rounded"></div>
            <div className="h-6 w-60 mr-2 bg-gray-300 rounded"></div>
          </div>
          <div className="h-6 w-full bg-gray-300 rounded mt-2"></div>
          <div className="h-6 w-80 bg-gray-300 rounded mt-2"></div>
          <div className="h-6 w-full bg-gray-300 rounded mt-2"></div>
          <div className="h-6 w-80 bg-gray-300 rounded mt-2"></div>
          <div className="h-6 w-full bg-gray-300 rounded mt-2"></div>
          <div className="h-6 w-full bg-gray-300 rounded mt-2"></div>
        </div>
      </div>
    );
  }

  // Role application logic - find pending application
  const appliedRole = candidateRoles.find(
    (roleObj) => roleObj.candidate === candidate.id && roleObj.stage === "Application pending"
  );
  
  // Check current status (including local changes)
  const currentStatus = localApplicationStatus || (appliedRole && appliedRole.stage);
  const hasApplied = currentStatus === "Application pending";
  const isAccepted = currentStatus === "Interviewing";
  const isRejected = currentStatus === "Rejected @ CV";
  
  // Create status style object
  const statusStyle = currentStatus ? statusStyles[currentStatus] : null;
  
  // Debug logging
  console.log("Debug info:", {
    candidateId: candidate.id,
    candidateRoles: candidateRoles.slice(0, 3), // Show first 3 roles for debugging
    totalRoles: candidateRoles.length,
    appliedRole,
    appliedRoleStage: appliedRole?.stage,
    hasApplicationPending: !!appliedRole,
    matchingRoles: candidateRoles.filter(role => role.candidate === candidate.id)
  });
  const roleAppliedTag =
    appliedRole ? (
      <span
        className={`absolute top-2 right-2 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium ${bgColor} ${textColor}`}
      >
        <>Applied to <strong>{appliedRole.role_title}</strong></>
      </span>
    ) : null;

  const roleAppliedButtons =
    appliedRole ? (
      <div className="flex mt-4 mb-4 w-full">
        {submitting ? (
          <div className="flex justify-center items-center w-full h-12 bg-gray-100 rounded-lg">
            <img src={Loading} className="h-6" alt="Loading" />
          </div>
        ) : (
          <>
            <button
              onClick={() => handleSubmit("Interviewing")}
              className="w-full group px-8 py-2 text-sm mr-1 text-black items-center rounded-lg font-medium transform hover:bg-white bg-[#02B491] hover:text-black text-white transition duration-400 border-2 border-[#02B491] flex flex-col justify-center items-center"
              title="We'll introduce you via email."
            >
              <span className="text-white group-hover:text-black">
                Accept Interview Request
              </span>
              <p className="flex items-center justify-center mt-1 text-white font-normal text-xs group-hover:text-black">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-4 h-4 mr-2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                  />
                </svg>
                You'll be introduced over email!
              </p>
            </button>

            <button
              onClick={() => handleSubmit("Rejected @ CV")}
              className="px-8 group py-2 text-sm ml-1 whitespace-nowrap rounded-lg font-medium transform bg-white hover:bg-gray-500 text-black hover:text-white transition duration-400 border-2 border-gray-500"
            >
              Not a match
            </button>
          </>
        )}
      </div>
    ) : null;

  const previewText =
    appliedRole?.rationale?.split(" ").slice(0, 10).join(" ") + "...";

  const roleAppliedRational =
    appliedRole && appliedRole.rationale ? (
      <div className={`pb-2 mt-2 px-2 rounded-md ${workBgColor}`}>
        <div className="font-normal text-md text-gray-700 pt-2">
          <span className="font-semibold text-black">
            Application Rationale:
          </span>{" "}
          {isExpanded ? appliedRole.rationale : previewText}
          <button onClick={handleToggle} className="text-blue-500 ml-2 underline">
            {isExpanded ? "Show Less" : "Read More"}
          </button>
        </div>
      </div>
    ) : null;

  const handleSubmit = async (stage) => {
    if (!appliedRole) return;
    const updatedData = {
      fields: { Stage: stage },
      interviewId: appliedRole.id, // Use the interview ID, not role_id
    };
    setError("");
    setSubmitting(true);
    try {
      await axios.patch(`/api/interviews/updateInterviewRequest`, updatedData);
      // Show success message
      console.log("Interview request updated successfully");
      
      // Update local status immediately
      setLocalApplicationStatus(stage);
      
      // Stay on the current page - no navigation
    } catch (err) {
      setError(err.message);
      console.error("Error updating interview request:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const workingStyleMapping = Array.isArray(candidate.working_style)
    ? candidate.working_style.map((style) => {
        if (style === "5 days a week in office") {
          return "Fully in office";
        } else if (style === "Hybrid (1-4 days in office)") {
          return "Hybrid";
        } else if (style === "Fully remote") {
          return "Fully remote";
        }
        return style;
      })
    : [];

  return (
    <div className="pt-12 lg:pl-72 lg:pr-8 bg-white min-h-screen px-4">
      <div className="flex flex-col lg:flex-row lg:gap-x-8 lg:items-start">
        {/* Main content */}
        <div className="w-full lg:max-w-[700px] flex-shrink-0">
          
          {/* Back button */}
          <div className="mb-4">
            <button
              type="button"
              onClick={handleBackToAllCandidates}
              className="px-4 py-2 text-gray-800 rounded-md flex items-center hover:bg-gray-100 -ml-4"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 mr-1">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              Back to all candidates
            </button>
          </div>

          <div className="flex flex-col items-center bg-white rounded-xl p-2 lg:p-4 mb-10 pb-10 border shadow-lg">
            <div className="w-full max-w-[700px] flex flex-col h-full bg-white gap-2">
              
              <div className="relative">
                {isVideoPlaying && candidate?.video ? (
                  <div 
                    className="video-player-container h-[250px] lg:h-[400px] w-full lg:w-[700px] relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isVideoLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
                        <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                    <iframe
                      src={getEmbeddedVideoUrl(candidate.video)}
                      className="h-[250px] lg:h-[400px] w-full lg:w-[700px] rounded-xl"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={`${candidate.name} video introduction`}
                      onLoad={handleVideoLoaded}
                    ></iframe>
                    <button 
                      className="absolute top-3 right-3 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-1 z-10"
                      onClick={toggleVideo}
                    >
                      <svg 
                        className="h-6 w-6 text-white" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <>
                    <img
                      src={candidate?.image}
                      className="h-[250px] lg:h-[400px] w-full lg:w-[700px] object-cover rounded-xl bg-[var(--overlay-2)]"
                      alt={candidate?.name}
                    />
                    {candidate?.video && (
                      <div 
                        className="absolute inset-0 flex items-center justify-center cursor-pointer"
                        onClick={toggleVideo}
                      >
                        <div className="relative bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full p-4 lg:p-5 transition-all duration-300 transform hover:scale-110">
                          <svg 
                            className="h-10 w-10 lg:h-14 lg:w-14 text-white" 
                            viewBox="0 0 24 24" 
                            fill="currentColor"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {roleAppliedTag}
              </div>

              <div className="flex flex-col flex-grow justify-between">
                {/* NAME + ICONS */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 lg:gap-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <h5 className="text-2xl lg:text-3xl font-bold tracking-tight text-gray-900">
                      {candidate?.name}
                    </h5>
                    <div className="flex items-center gap-2">
                      {candidate?.linkedin && (
                        <img
                          src={linkedin}
                          alt="LinkedIn"
                          title="LinkedIn"
                          className="h-6 w-6 cursor-pointer"
                          onClick={() => window.open(candidate.linkedin, "_blank")}
                        />
                      )}
                      {candidate?.cv && (
                        <img
                          src={cv}
                          alt="CV"
                          title="CV"
                          className="h-6 w-6 cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(candidate.cv, "_blank");
                          }}
                        />
                      )}
                      {candidate?.github && (
                        <img
                          src={github}
                          alt="GitHub"
                          title="GitHub"
                          className="h-6 w-6 cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(candidate.github, "_blank");
                          }}
                        />
                      )}
                      {candidate.portfolio && (
                        <svg
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(candidate.portfolio, "_blank");
                          }}
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="size-6 cursor-pointer"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col text-left lg:text-right">
                    <span className="text-sm lg:text-base">Expected Salary Range:</span>
                    {candidate.minimum_salary && (
                      <h5 className="text-md font-semibold tracking-tight text-gray-900">
                        {candidate.minimum_salary} - {candidate.ideal_salary}
                      </h5>
                    )}
                  </div>
                </div>

                {/* Roles / Sales / SWE Roles, etc. */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {candidate.roles &&
                    candidate.roles.map((roleVal, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm lg:text-md font-medium ${bgColor} ${textColor}`}
                      >
                        {roleVal}
                      </span>
                    ))}
                  {candidate.sales && (
                    <span
                      className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm lg:text-md font-medium ${bgColor} ${textColor}`}
                    >
                      {candidate.sales}
                    </span>
                  )}
                  {candidate.swe_roles &&
                    candidate.swe_roles.map((r, idx) => (
                      <span
                        key={idx}
                        className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm lg:text-md font-medium ${bgColor} ${textColor}`}
                      >
                        {r}
                      </span>
                    ))}
                  {candidate.exp_swe && (
                    <span
                      className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm lg:text-md font-medium ${bgColor} ${textColor}`}
                    >
                      {candidate.exp_swe}
                    </span>
                  )}
                  {candidate.time_coding && (
                    <span
                      className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm lg:text-md font-medium ${bgColor} ${textColor}`}
                    >
                      {candidate.time_coding} yrs of coding exp
                    </span>
                  )}

                  {/* If you still want to show "No - Requires Visa sponsorship" as a badge, keep this logic:
                      (But we are now doing a custom message above, so up to you.)
                  */}
                  {candidate.right_to_work &&
                    validRightToWorkStatuses.includes(candidate.right_to_work) && (
                      <span
                        className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm lg:text-md font-medium ${bgColor} ${textColor}`}
                      >
                        {candidate.right_to_work}
                      </span>
                    )}
                </div>

                {/* Tech stack icons */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {candidate.coding_languages &&
                    candidate.coding_languages.map((language, index) => (
                      <img
                        key={index}
                        src={languageLogos[language.trim()] || "/images/coding_logo/default.png"}
                        alt={language}
                        className="h-6 w-6"
                      />
                    ))}
                </div>
                {candidate["Jumpstart view (Sales)"] && (
                  <div className="mt-4">
                    <div
                      className={`rounded-lg overflow-hidden transition-all duration-300 ${
                        showJumpstartNotes
                          ? 'shadow-md border border-emerald-100'
                          : 'border border-transparent hover:border-emerald-100'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card navigation
                        setShowJumpstartNotes(!showJumpstartNotes);
                      }}
                      onMouseEnter={() => setShowJumpstartNotes(true)}
                      onMouseLeave={() => setShowJumpstartNotes(false)}
                    >
                      {/* Header */}
                      <div
                        className={`flex items-center justify-between p-3 cursor-pointer ${
                          showJumpstartNotes
                            ? 'bg-gradient-to-r from-emerald-50 to-emerald-100/30'
                            : 'bg-emerald-50/60 hover:bg-emerald-50'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded bg-emerald-100">
                            <img
                              src={jumpstart}
                              alt="Jumpstart"
                              className="h-4 w-4"
                            />
                          </div>
                          <span className="font-medium text-sm text-gray-800">Jumpstart's POV</span>
                        </div>
                        <div className={`rounded-full p-1 ${showJumpstartNotes ? 'bg-emerald-100/80' : 'bg-transparent'}`}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className={`w-4 h-4 text-emerald-600 transition-transform duration-300 ${
                              showJumpstartNotes ? 'rotate-180 transform' : ''
                            }`}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                        </div>
                      </div>
                
                      {/* Content - animate height */}
                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          showJumpstartNotes ? 'max-h-96' : 'max-h-0'
                        }`}
                      >
                        <div className="p-3 pt-2 bg-white border-t border-emerald-100">
                          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                            {candidate["Jumpstart view (Sales)"]}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Working style & languages */}
                <div className="flex flex-col lg:flex-row gap-3 lg:gap-5 pb-2 pt-3">
                  {candidate.working_style &&
                    Array.isArray(candidate.working_style) &&
                    workingStyleMapping.length > 0 && (
                      <p className="font-normal flex text-sm lg:text-md text-black items-center">
                        <img className="h-6 pr-1" src={Office} alt="Office icon" />
                        {workingStyleMapping.join(" or ")}
                      </p>
                    )}
                  {candidate.languages &&
                    Array.isArray(candidate.languages) &&
                    candidate.languages.some((lang) => lang !== "N/A") && (
                      <p className="font-normal flex text-sm lg:text-md text-black items-center">
                        <img className="h-6 pr-2" src={Languages} alt="Languages icon" />
                        {candidate.languages.filter((lang) => lang !== "N/A").join(", ")}
                      </p>
                    )}
                </div>

                {/* Education section with improved mobile spacing */}
                <div className="space-y-2 mt-2">
                  {candidate.undergrad_degree_type && (
                    <p className="text-sm lg:text-md">
                      <span className="font-semibold">
                        {candidate.undergrad_degree_type} {candidate.undergrad_degree_subject}
                      </span>
                      {candidate.undergrad_university && " @ "}
                      {candidate.undergrad_university} ({candidate.undergrad_result}
                      {candidate.undergrad_result && candidate.undergrad_graduation_year && " - "}
                      {candidate.undergrad_graduation_year})
                    </p>
                  )}

                  {candidate.postgrad_degree_type && (
                    <p className="text-sm lg:text-md">
                      <span className="font-semibold">
                        {candidate.postgrad_degree_type} {candidate.postgrad_degree_subject}
                      </span>
                      {candidate.postgrad_university && " @ "}
                      {candidate.postgrad_university} ({candidate.postgrad_result}
                      {candidate.postgrad_result && candidate.postgrad_graduation_year && " - "}
                      {candidate.postgrad_graduation_year})
                    </p>
                  )}
                </div>

                {/* Work experience container */}
                <div className={`pb-2 mt-4 px-3 rounded-md ${workBgColor}`}>
                  {candidate.recentTarget && (
                    <div className="font-normal text-sm text-gray-700 pt-2">
                      <span className="font-semibold text-black">
                        Recent Target Achieved:
                      </span>{" "}
                      {candidate.recentTarget}
                    </div>
                  )}

                  {candidate.work_experience_1_company && (
                    <>
                      <p className="text-sm pt-2">
                        <span className="font-semibold">
                          {candidate.work_experience_1_role}
                        </span>{" "}
                        @ {candidate.work_experience_1_company}
                        {candidate.work_experience_1_duration &&
                          ` (${formatDuration(
                            candidate.work_experience_1_duration
                          )})`}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {candidate.work_experience_1_description}
                      </p>
                    </>
                  )}

                  {candidate.work_experience_2_company && (
                    <>
                      <p className="text-sm pt-2">
                        <span className="font-semibold">
                          {candidate.work_experience_2_role}
                        </span>{" "}
                        @ {candidate.work_experience_2_company}
                        {candidate.work_experience_2_duration &&
                          ` (${formatDuration(
                            candidate.work_experience_2_duration
                          )})`}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {candidate.work_experience_2_description}
                      </p>
                    </>
                  )}
                </div>

                {/* Most proud of section */}
                {candidate.description && (
                  <div className="mt-4 font-normal text-sm lg:text-md text-gray-700 flex items-start">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.7"
                      stroke="currentColor"
                      className="size-5 mr-2 flex-shrink-0 mt-1"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1-3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
                      />
                    </svg>
                    <p>{candidate.description}</p>
                  </div>
                )}



                {/* Visa Status Component - Interactive like other components */}
                {visaMessage && (
                  <div className="mt-6">
                    <div 
                      className={`rounded-lg overflow-hidden transition-all duration-300 ${
                        showVisaTooltip 
                          ? 'shadow-md border border-gray-100' 
                          : 'border border-transparent hover:border-gray-100'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent any parent click handlers
                        setShowVisaTooltip(!showVisaTooltip);
                      }}
                      onMouseEnter={() => setShowVisaTooltip(true)}
                      onMouseLeave={() => setShowVisaTooltip(false)}
                    >
                      {/* Header */}
                      <div 
                        className={`flex items-center justify-between p-3 cursor-pointer ${
                          showVisaTooltip 
                            ? 'bg-gradient-to-r from-gray-50 to-gray-100/30' 
                            : 'bg-gray-50/60 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded bg-gray-100">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-600">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5zm6-10.125a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0zm1.294 6.336a6.721 6.721 0 0 1-3.17.789 6.721 6.721 0 0 1-3.168-.789 3.376 3.376 0 0 1 6.338 0z" />
                            </svg>
                          </div>
                          <span className="font-medium text-sm text-gray-800">Visa Status</span>
                        </div>
                        <div className={`rounded-full p-1 ${showVisaTooltip ? 'bg-gray-100/80' : 'bg-transparent'}`}>
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            strokeWidth={2} 
                            stroke="currentColor" 
                            className={`w-4 h-4 text-gray-600 transition-transform duration-300 ${
                              showVisaTooltip ? 'rotate-180 transform' : ''
                            }`}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                        </div>
                      </div>
                      
                      {/* Content - animate height */}
                      <div 
                        className={`overflow-hidden transition-all duration-300 ${
                          showVisaTooltip ? 'max-h-96' : 'max-h-0'
                        }`}
                      >
                        <div className="p-3 pt-2 bg-white border-t border-gray-100">
                          <p 
                            className="text-sm text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: visaMessage }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Application Rationale */}
                {roleAppliedRational}
                
                {/* Status display (if any) */}
                {statusStyle && (
                  <div
                    className={`flex justify-between items-center py-2 px-2 mt-2 mb-4 text-sm ${statusStyle.color} border ${statusStyle.borderColor} rounded-lg ${statusStyle.bgColor}`}
                    role="alert"
                  >
                    <div className="flex items-center">
                      <svg
                        className="flex-shrink-0 inline w-4 h-4 mr-1"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
                      </svg>
                      <span className="sr-only">Info</span>
                      <div>{statusStyle.message(candidate, appliedRole?.role_title || 'Unknown Role')}</div>
                    </div>
                  </div>
                )}
                
                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right column for similar candidates & actions */}
        <div className="w-full lg:w-80 flex-shrink-0">
          
          {/* Action buttons */}
          <div className="flex justify-end mb-4 mt-4 lg:mt-14">
            {hasApplied ? (
              <div className="flex flex-col gap-2 w-full">
                {submitting ? (
                  <div className="flex justify-center items-center w-full h-12 bg-gray-100 rounded-lg">
                    <img src={Loading} className="h-6" alt="Loading" />
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => handleSubmit("Interviewing")}
                      className="w-full group px-6 py-3 text-sm text-black items-center rounded-lg font-medium transform hover:bg-white bg-[#02B491] hover:text-black text-white transition duration-400 border-2 border-[#02B491] flex flex-col justify-center items-center"
                      title="We'll introduce you via email."
                    >
                      <span className="text-white group-hover:text-black">
                        Accept Interview Request
                      </span>
                      <p className="flex items-center justify-center mt-1 text-white font-normal text-xs group-hover:text-black">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="w-4 h-4 mr-2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                          />
                        </svg>
                        You'll be introduced over email!
                      </p>
                    </button>

                    <button
                      onClick={() => handleSubmit("Rejected @ CV")}
                      className="w-full px-6 group py-3 text-sm rounded-lg font-medium transform bg-white hover:bg-gray-500 text-black hover:text-white transition duration-400 border-2 border-gray-500"
                    >
                      Not a match
                    </button>
                  </>
                )}
              </div>
            ) : isAccepted ? (
              <div className="w-full">
                <div className="w-full px-4 py-3 bg-green-50 border-2 border-green-300 rounded-lg text-center">
                  <div className="flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-5 text-green-600 mr-2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-green-800 font-medium">Application Accepted</span>
                  </div>
                  <p className="text-green-600 text-xs mt-1">You'll be introduced over email</p>
                </div>
              </div>
            ) : roleShortlistedFor ? (
              <div className="flex items-center justify-center w-full">
                <button
                  onClick={() => handleRemoveCandidate(shortlistedCandidates, roleShortlistedFor, candidate.id)}
                  className="w-full px-6 py-2 text-md border-2 text-black border-red-500 bg-white rounded-lg font-medium hover:bg-red-500 hover:text-white"
                  disabled={buttonLoading}
                >
                  {buttonLoading ? (
                    <div className="flex justify-center items-center w-full rounded-lg">
                      <img src={LoadingBlack} className="h-6 fill-red-500" alt="buttonLoading" />
                    </div>
                  ) : (
                    `Remove from ${roleShortlistedFor} shortlist`
                  )}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center w-full">
                <div className="flex gap-2 w-full">
                  <div className="relative w-1/2">
                    <button
                      onClick={handleAddToShortlist}
                      className="w-full h-10 px-3 py-2 text-sm text-black flex items-center rounded-lg font-medium justify-center hover:bg-[#02B491] hover:text-white border-2 border-[#02B491] bg-white"
                    >
                      {buttonLoading ? (
                        <div className="flex justify-center items-center w-full rounded-lg">
                          <img src={LoadingBlack} className="h-6" alt="Loading" />
                        </div>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="size-5 mr-1"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"
                            />
                          </svg>
                          <span>Shortlist</span>
                        </>
                      )}
                    </button>
                    {showRoleMenu && (
                      <div
                        ref={roleMenuRef}
                        className="absolute top-full mt-2 left-0 w-48 bg-white shadow-lg rounded-lg z-10"
                      >
                        {roles.map((roleObj, index) => (
                          <button
                            key={index}
                            onClick={() => handleRoleSelect(roleObj, candidate.id)}
                            className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {roleObj["Role Title"]}
                          </button>
                        ))}
                        <button
                          onClick={newRole}
                          className="block w-full px-4 py-2 text-left bg-[#02B491] rounded-b-lg text-sm text-white hover:bg-[#00f2c2] hover:text-black"
                        >
                          + New Role
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="relative w-1/2">
                    <button
                      onClick={handleMatchRequestClick}
                      className={`w-full h-10 px-3 py-2 text-sm text-black flex items-center rounded-lg font-medium justify-center whitespace-nowrap
                      ${roleMatchRequestedFor
                          ? 'border-2 border-red-500 hover:bg-red-500 hover:text-white'
                          : 'hover:bg-emerald-400 hover:text-white border-2 border-emerald-400'} 
                      bg-white`}
                      disabled={requestLoading || roleMatchRequestedFor}
                      title={roleMatchRequestedFor ? "You have already sent a match request to this candidate" : "Send a match request to this candidate"}
                    >
                      {requestLoading ? (
                        <div className="flex justify-center items-center w-full rounded-lg">
                          <img src={LoadingBlack} className="h-6" alt="Loading" />
                        </div>
                      ) : roleMatchRequestedFor ? (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="size-5 mr-1"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>Requested</span>
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="size-5 mr-1"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                            />
                          </svg>
                          <span>Request</span>
                        </>
                      )}
                    </button>
                    {showMatchRequestRoleMenu && (
                      <div
                        ref={matchRequestRoleMenuRef}
                        className="absolute top-full mt-2 right-0 w-48 bg-white shadow-lg rounded-lg z-10"
                      >
                        {Array.isArray(roles) ? (
                          roles.map((role, index) => (
                            <button
                              key={index}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMatchRequestRoleSelect(role);
                              }}
                              className={`block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${index === roles.length - 1 ? 'rounded-b-lg' : ''}`}
                            >
                              {role["Role Title"]}
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-sm text-gray-500 rounded-b-lg">No roles available</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {newRoleForm && (
                  <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 px-4">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm">
                      <h2 className="text-lg font-semibold mb-4">Add New Role</h2>
                      <form onSubmit={handleNewRoleSubmit}>
                        <label htmlFor="title" className="block text-md font-normal leading-6 text-gray-900">
                          Role Title
                        </label>
                        <input
                          type="text"
                          value={newRoleTitle}
                          onChange={(e) => setNewRoleTitle(e.target.value)}
                          placeholder="Sales"
                          className="w-full p-2 mb-4 border border-gray-300 rounded"
                          required
                        />
                        <div className="flex flex-col gap-2">
                          <button
                            type="submit"
                            className="w-full px-4 py-2 bg-[#02B491] text-sm text-white rounded hover:bg-[#00f2c2] hover:text-black"
                          >
                            Submit
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewRoleForm(false)}
                            className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded border border-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 lg:mt-0">
  <button
    onClick={() => setIsSimilarCandidatesExpanded(!isSimilarCandidatesExpanded)}
    className="w-full flex justify-between items-center text-left py-2 rounded-lg hover:bg-gray-50 focus:outline-none"
  >
              <h3 className="text-sm font-semibold text-gray-800">See similar candidates</h3>
              <svg
                className={`w-6 h-6 text-gray-600 transition-transform transform ${
                  isSimilarCandidatesExpanded ? 'rotate-180' : ''
                }`}
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
            
            <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isSimilarCandidatesExpanded ? 'max-h-[2000px] mt-4' : 'max-h-0'}`}>
              {similarCandidatesLoading && (
                <div className="space-y-3 px-1">
                  {/* Loading skeleton cards */}
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse bg-gray-50 rounded-lg p-3 border">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-300 rounded-full"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {similarCandidatesError && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                  {similarCandidatesError}
                </div>
              )}

              {!similarCandidatesLoading && !similarCandidatesError && similarCandidates.length === 0 && (
                <div className="text-gray-500 text-sm bg-gray-50 p-3 rounded-lg border px-1">
                  No similar candidates found in this stream.
                </div>
              )}

              {!similarCandidatesLoading && similarCandidates.length > 0 && (
                <div className="space-y-3 px-0.5">
                  {similarCandidates.map((item, index) => {
                    const similarCandidate = item.candidate;
                    const fields = similarCandidate.fields || {};
                    
                    // Get stream-based colors for the similar candidate
                    const stream = fields["Stream (from CRM)"]?.[0] || "";
                    const { bgColor: simBgColor, textColor: simTextColor } = getColorClasses(stream);
                    
                    return (
                      <div 
                        key={similarCandidate.id} 
                        className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200 cursor-pointer"
                        onClick={() => {
                          const candidateUrlId = fields["ID"] || similarCandidate.id;
                          window.open(`/candidate/${candidateUrlId}`, '_blank');
                        }}
                      >
                        <div className="p-3 relative">
                              {/* Header with external link icon */}
                              <div className="absolute top-3 right-3">
                            <svg 
                              className="w-4 h-4 text-gray-400" 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </div>
                          
                              <div className="flex items-center space-x-3">
                                <div className="w-12 h-12 bg-gray-300 rounded-full flex-shrink-0 overflow-hidden">
                              {fields.Photo && fields.Photo[0] && (
                                <img 
                                  src={fields.Photo[0].url} 
                                  alt={fields.Name || 'Candidate'} 
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-lg text-black">
                                {fields.Name || 'Unknown'}
                              </h4>
                                    <div className="flex items-center gap-2">
                                      {fields["LinkedIn RAW"] && (
                                        <img
                                          src={linkedin}
                                          alt="LinkedIn"
                                          title="LinkedIn"
                                          className="h-5 w-5 cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(fields["LinkedIn RAW"], "_blank");
                                          }}
                                        />
                                      )}
                                      {fields["CV RAW"] && (
                                        <img
                                          src={cv}
                                          alt="CV"
                                          title="CV"
                                          className="h-5 w-5 cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(fields["CV RAW"], "_blank");
                                          }}
                                        />
                                      )}
                                      {fields["Video intro (new)"] && (
                                        <img
                                          src={VideoIntro}
                                          alt="Video Intro"
                                          title="Video Intro"
                                          className="h-5 w-5 cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(fields["Video intro (new)"], "_blank");
                                          }}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="text-sm text-black space-y-2 mt-3">
                                {fields["(1) Work Experience Role"] && (
                                    <div className="leading-relaxed">
                                      <span className="font-medium">{fields["(1) Work Experience Role"]}</span>
                                    {fields["(1) Work Experience Company"] && (
                                      <span> @ {fields["(1) Work Experience Company"]}</span>
                                    )}
                                      {fields["(1) Time spent in work experience YRS"] && fields["(1) Time spent in work experience MOS"] && (
                                        <span> ({formatDuration({
                                          years: fields["(1) Time spent in work experience YRS"],
                                          months: fields["(1) Time spent in work experience MOS"]
                                        })})</span>
                                      )}
                                  </div>
                                )}
                                
                                  {fields["(2) Work Experience Role"] && (
                                    <div className="leading-relaxed">
                                      <span className="font-medium">{fields["(2) Work Experience Role"]}</span>
                                      {fields["(2) Work Experience Company"] && (
                                        <span> @ {fields["(2) Work Experience Company"]}</span>
                                      )}
                                      {fields["(2) Time spent in work experience YRS"] && fields["(2) Time spent in work experience MOS"] && (
                                        <span> ({formatDuration({
                                          years: fields["(2) Time spent in work experience YRS"],
                                          months: fields["(2) Time spent in work experience MOS"]
                                        })})</span>
                              )}
                            </div>
                                )}
                              </div>
                                
                              <div className="w-full mt-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const candidateUrlId = fields["ID"] || similarCandidate.id;
                                    window.open(`/candidate/${candidateUrlId}`, '_blank');
                                  }}
                                  className="w-full px-3 py-2 text-sm border border-[#02B491] text-[#02B491] bg-white rounded-md font-medium hover:bg-[#02B491] hover:text-white transition-colors"
                                >
                                  View profile
                                </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* RoleMatchRequestModal */}
      <RoleMatchRequestModal 
        isOpen={showMatchRequestModal}
        onClose={() => setShowMatchRequestModal(false)}
        candidate={candidate}
        selectedRole={selectedRoleForModal}
        onRequestSent={handleRequestSent}
      />
    </div>
  );
}

export default Candidate;
